import React, { useMemo } from 'react';

import { HELP_CENTERS_MANAGEMENT_BASE_PATH } from '@helpcenter/constants';
import { getHelpCenterAri } from '@helpcenter/meta';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { useHubBasePath } from '@atlassian/company-hub-common/use-hub-base-path';
import { useShouldShowPremiumUpsell } from '@atlassian/company-hub-common/use-should-show-premium-upsell';

import { parseHelpCenterAri } from '../../../utils/ari-parser';
import type { HubCustomizeMenuItem, HubCustomizeMenuSection } from '../hub-customize-menu';
import hubMessages from '../messages';
import { PremiumUpsellMenuItem } from '../premium-upsell-menu-item';

/**
 * The "Help center" group of the Customise menu — the site-wide entry points that
 * are the same on every page. Returns `null` for non-admins, which is what hides
 * the group (and, when it is the only group, the whole menu).
 *
 * Help center management is moving to the Jira side, so the same screens live at
 * two addresses while that ramps. `useHubBasePath` owns which one is live — it
 * reads the `jira_sidebar_show_helpcenter` experiment — and the two worlds do not
 * merely differ by prefix, so the base path also tells us which URL shapes to
 * build. Reading it from there keeps this off a second experiment lookup.
 *
 * Creating help centers and custom pages is Premium-only. Sites without it get a
 * Premium lozenge on those two items, and clicking one opens the edition benefits
 * modal rather than a screen they could not use. `useShouldShowPremiumUpsell` is
 * the same gate the Fonts tab upsell uses, so both ramp together and Customer
 * Service hubs are excluded in one place.
 */
export const useHelpCenterCustomizeSection = (
	canAdministerJIRA: boolean,
): HubCustomizeMenuSection | null => {
	di(getHelpCenterAri, useHubBasePath, useShouldShowPremiumUpsell);
	const { formatMessage } = useIntl();
	const basePath = useHubBasePath();
	const shouldUpsellPremium = useShouldShowPremiumUpsell();

	return useMemo(() => {
		if (!canAdministerJIRA) {
			return null;
		}

		const { helpCenterId } = parseHelpCenterAri(getHelpCenterAri());
		// Same check `hub-home` makes off this base path to pick its Rovo settings URL.
		const isInJira = basePath.startsWith('/jira');

		// Jira lists help centers at the base path itself; the help-center-hosted
		// screen lists them under /hubs.
		const managementUrl = isInJira ? basePath : `${basePath}/hubs`;

		// Both screens open their create flow from the same query param.
		const createHelpCenterUrl = `${managementUrl}?create=true`;

		const rovoAgentUrl = isInJira
			? `${basePath}/${helpCenterId}/settings?tab=rovo-agent`
			: `${basePath}/${helpCenterId}/rovo-agent`;

		// Deliberately not built from `basePath`: there is no Jira-side editor route,
		// so this stays on the help-center-hosted path in both worlds — the same thing
		// every `edit-hub` URL in `hub-home` does. It always opens the hub home page,
		// per the design, even when triggered from another page.
		const createCustomPageUrl = `${HELP_CENTERS_MANAGEMENT_BASE_PATH}/${helpCenterId}/edit-hub?createPage=true`;

		// Premium-gated: these two upsell instead of navigating when the site is not
		// entitled, so an admin is not sent to a screen they cannot use.
		const createHelpCenter: HubCustomizeMenuItem = shouldUpsellPremium
			? {
					label: formatMessage(hubMessages.createHelpCenterLabel),
					testId: 'create-help-center-upsell',
					onClick: () => undefined,
					customRenderer: () => (
						<PremiumUpsellMenuItem
							label={formatMessage(hubMessages.createHelpCenterLabel)}
							testId="create-help-center-upsell"
							touchpointId="hubs-customise-menu-create-help-center"
						/>
					),
				}
			: {
					label: formatMessage(hubMessages.createHelpCenterLabel),
					testId: 'create-help-center-link',
					href: createHelpCenterUrl,
					actionSubjectId: 'navbarCreateHelpCenter',
				};

		const createCustomPage: HubCustomizeMenuItem = shouldUpsellPremium
			? {
					label: formatMessage(hubMessages.createCustomPageLabel),
					testId: 'create-custom-page-upsell',
					onClick: () => undefined,
					customRenderer: () => (
						<PremiumUpsellMenuItem
							label={formatMessage(hubMessages.createCustomPageLabel)}
							testId="create-custom-page-upsell"
							touchpointId="hubs-customise-menu-create-custom-page"
						/>
					),
				}
			: {
					label: formatMessage(hubMessages.createCustomPageLabel),
					testId: 'create-custom-page-link',
					href: createCustomPageUrl,
					actionSubjectId: 'navbarCreateCustomPage',
				};

		return {
			heading: formatMessage(hubMessages.helpCenterSectionHeading),
			items: [
				{
					label: formatMessage(hubMessages.viewAllHelpCentersLabel),
					testId: 'view-all-help-centers-link',
					href: managementUrl,
					actionSubjectId: 'navbarViewAllHelpCenters',
				},
				createHelpCenter,
				createCustomPage,
				{
					label: formatMessage(hubMessages.rovoAgentLabel),
					testId: 'rovo-agent-link',
					href: rovoAgentUrl,
					actionSubjectId: 'navbarRovoAgent',
				},
			],
		};
	}, [canAdministerJIRA, formatMessage, basePath, shouldUpsellPremium]);
};
